// src/hooks/useList/index.ts
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { Services } from "$utils/typeConstants";
import { appState } from "$reducers";
import { useListParams } from "./useListParams";
import { useRequest } from "./useRequest";
import { UseListOptions, UseListResult } from "./types";

/**
 * Main hook for fetching and managing list data
 */
export function useList<T extends { id?: string | number }>(
  endpoint: string,
  options: UseListOptions & { cacheKey?: string } = {}
): UseListResult<T> {
  const {
    defaultFilters,
    defaultSort,
    defaultPageSize,
    populate,
    fields,
    additionalParams,
    ignoreSideBarFilter = false,
    service = Services.MAIN_API,
    enabled = true,
    cacheKey,
    queryOptions: userQueryOptions,
  } = options;

  // Get company filter from Redux store if needed
  const { filter: companySidebarFilter } = useSelector(appState);

  // Manage list parameters
  const {
    params,
    setPageIndex,
    setPageSize,
    setFilter,
    setFilters: setAllFilters,
    setSort,
    resetParams,
  } = useListParams({
    defaultFilters,
    defaultSort,
    defaultPageSize,
    populate,
    fields,
  });

  // Use React Query for data fetching
  const {
    data: queryData,
    isLoading,
    isError,
    isFetching,
    isRefetching,
    error,
    refetch,
    addItem,
    removeItem,
    setQueryData,
  } = useRequest<T>(endpoint, params, {
    service,
    additionalParams,
    companySidebarFilter,
    ignoreSideBarFilter,
    cacheKey,
    queryOptions: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      enabled,
      ...userQueryOptions,
    },
  });

  // Get data and pagination info
  // Handle consolidated endpoint structure: { data: { data: [...], companies: [...], ... }, meta: {...} }
  let data: T[] = [];

  if (Array.isArray(queryData)) {
    data = queryData;
  } else if (queryData && typeof queryData === "object") {
    if ("data" in queryData) {
      const responseData = queryData.data;
      if (Array.isArray(responseData)) {
        data = responseData;
      } else if (
        responseData &&
        typeof responseData === "object" &&
        "data" in responseData
      ) {
        // Consolidated endpoint structure: { data: { data: [...], companies: [...], ... } }
        const consolidatedData = (responseData as any).data;
        data = Array.isArray(consolidatedData) ? consolidatedData : [];
      }
    }
  }

  const pagination = queryData?.meta?.pagination || {
    total: 0,
    pageCount: 0,
    page: params.pagination.pageIndex + 1,
    pageSize: params.pagination.pageSize,
  };

  // Update item in local data without API call
  const updateLocalItem = useCallback(
    (id: string | number, updatedItem: Partial<T>) => {
      if (!queryData) return;

      const newData = {
        ...queryData,
        data: queryData.data.map((item) =>
          item.id === id ? { ...item, ...updatedItem } : item
        ),
      };

      // Update local cache without API call
      setQueryData(newData);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [queryData, setQueryData]
  );

  // Handle list item removal from the local array
  const removeItemFromList = useCallback(
    (id: string | number) => {
      if (!queryData) return;

      removeItem(id);
    },
    [queryData, removeItem]
  );

  // Add item to the list
  const addItemToList = useCallback(
    (item: T) => {
      addItem(item);
    },
    [addItem]
  );

  // Convenient API to return to consumers
  return {
    // Data
    data: data as T[],
    setData: (newData: T[]) => {
      if (!queryData) return;

      const updatedQueryData = {
        ...queryData,
        data: newData,
      };

      setQueryData(updatedQueryData);
    },

    // Status
    isLoading,
    isFetching,
    isRefetching,
    isError,
    error,

    // Pagination
    pagination: {
      pageIndex: params.pagination.pageIndex,
      pageSize: params.pagination.pageSize,
      pageCount: pagination.pageCount,
      total: pagination.total,
      setPageIndex,
      setPageSize,
    },

    // Filters
    filters: params.filters,
    setFilter,
    setFilters: setAllFilters,

    // Sorting
    sort: params.sort,
    setSort,

    // Operations
    refetch: async () => {
      await refetch();
      // Suppress unused variable warning
      resetParams as unknown;
    },
    removeItem: removeItemFromList,
    addItem: addItemToList,
    updateItem: updateLocalItem,
  };
}

// Default export
export default useList;
